import React, { useLayoutEffect, useRef } from "react";
import useOcReveal from "../useOcReveal";
import "./Ecosystem.scss";

import boxBg from "../../../assets/images/home/05-ecosystem/ecosystem-box-only.png";
import ocStack from "../../../assets/images/home/05-ecosystem/oc-stack.png";
import lightUp from "../../../assets/images/home/05-ecosystem/llight-up.svg";
import grady from "../../../assets/images/home/05-ecosystem/grady-thinking.png";
import learnersImg from "../../../assets/images/home/05-ecosystem/image-learners-reached.png";
import ocid from "../../../assets/images/home/05-ecosystem/image-ocid.svg";

// Full wordmarks; sizes match Figma "Image Ecosystem Logos" (2770:34226).
import logoTinyTap from "../../../assets/images/home/07-partners/logo-tiny-tap-2.svg";
import logoNewCampus from "../../../assets/images/home/07-partners/logo-new-campus-2.svg";
import logoRiseIn from "../../../assets/images/home/07-partners/logo-rise-in-2.svg";
import logoHackquest from "../../../assets/images/home/07-partners/logo-hackquest-2.svg";
import logoBinance from "../../../assets/images/home/07-partners/logo-binance-labs-2.svg";

const ECO_LOGOS = [
  { src: logoHackquest, alt: "HackQuest", w: 100.5, h: 12 },
  { src: logoTinyTap, alt: "TinyTap", w: 40, h: 40 },
  { src: logoNewCampus, alt: "NewCampus", w: 57.5, h: 24 },
  { src: logoBinance, alt: "Binance Labs", w: 78.8, h: 20 },
  { src: logoRiseIn, alt: "Rise In", w: 60, h: 20 },
];

export default function Ecosystem() {
  const sectionRef = useRef(null);
  useOcReveal(sectionRef);

  // The fold is a fixed 1440×900 Figma composition. Scale the whole stage to
  // the available width so the rounded "Ecosystem Box" (with its ring + shadow)
  // always stays fully in frame, at exact Figma proportions.
  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    const DESIGN_W = 1440;
    const DESIGN_H = 900;

    const apply = () => {
      const width = section.clientWidth;
      // Below 1024px hand off to the stacked responsive layout (CSS media
      // query) so text stays legible instead of shrinking with the stage.
      if (width < 1024) {
        section.style.removeProperty("--eco-scale");
        section.style.removeProperty("height");
        return;
      }
      const scale = Math.min(1, width / DESIGN_W);
      section.style.setProperty("--eco-scale", String(scale));
      section.style.height = `${DESIGN_H * scale}px`;
    };

    apply();

    const ro = new ResizeObserver(apply);
    ro.observe(section);
    window.addEventListener("resize", apply);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", apply);
    };
  }, []);

  return (
    <section className="ecosystem" id="about" ref={sectionRef}>
      <div className="ecosystem__stage">
      <div className="ecosystem__box">
        {/* Exported box chrome (soft bloom + white ring + lilac shadow) — 2680×1700 @2x */}
        <img className="ecosystem__box-bg" src={boxBg} alt="" aria-hidden="true" />

        <div className="ecosystem__clip">
          {/* Ambient glows — Figma Ellipse 1409 / 1410 / 1411 (coords relative to OC Graphic) */}
          <span className="ecosystem__glow ecosystem__glow--purple" aria-hidden="true" />
          <span className="ecosystem__glow ecosystem__glow--teal" aria-hidden="true" />

          <img
            className="ecosystem__stack oc-reveal"
            src={ocStack}
            alt="Open Campus stack"
            style={{ "--oc-delay": "0.08s" }}
          />
          {/* Green "Llight up" gradient — Figma paints it AFTER the OC Stack (on top) */}
          <img
            className="ecosystem__light oc-reveal"
            src={lightUp}
            alt=""
            aria-hidden="true"
            style={{ "--oc-delay": "0.08s" }}
          />

          {/* White bottom fade — blends the stack into the box */}
          <span className="ecosystem__glow ecosystem__glow--fade" aria-hidden="true" />

          <div className="ecosystem__heading oc-reveal">
            <h2>Built on years of education impact</h2>
            <p>
              Open Campus is bringing its learner network, partner ecosystem, and credential
              infrastructure into a new AI-first experience.
            </p>
          </div>

          <img
            className="ecosystem__grady oc-reveal"
            src={grady}
            alt=""
            aria-hidden="true"
            style={{ "--oc-delay": "0.08s" }}
          />

          <div className="ecosystem__stats">
            <div className="eco-stat eco-stat--learners oc-reveal" style={{ "--oc-delay": "0.18s" }}>
              <div className="eco-stat__num">60 million</div>
              <div className="eco-stat__label">Learners reached</div>
              <p className="eco-stat__desc">
                Open Campus' education ecosystem connects partners with a large addressable learner
                network across learning, career, and growth.
              </p>
              <img className="eco-stat__faces" src={learnersImg} alt="" aria-hidden="true" />
            </div>

            <div className="eco-stat eco-stat--companies oc-reveal" style={{ "--oc-delay": "0.26s" }}>
              <div className="eco-stat__num">80+</div>
              <div className="eco-stat__label">Ecosystem companies</div>
              <p className="eco-stat__desc">
                A growing network of schools, platforms, and organizations have worked with Open Campus
                to expand access to learning.
              </p>
              {/* Ticker-tape marquee: the track holds two copies of the logo
                  set and slides by -50% for a seamless loop. */}
              <div className="eco-stat__logos">
                <div className="eco-stat__logos-track">
                  {[...ECO_LOGOS, ...ECO_LOGOS].map((l, i) => (
                    <img
                      key={`${l.alt}-${i}`}
                      src={l.src}
                      alt={i < ECO_LOGOS.length ? l.alt : ""}
                      aria-hidden={i >= ECO_LOGOS.length}
                      style={{ width: `${l.w}px`, height: `${l.h}px` }}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="eco-stat eco-stat--ocid oc-reveal" style={{ "--oc-delay": "0.34s" }}>
              <div className="eco-stat__num">300K+</div>
              <div className="eco-stat__label">OCIDs claimed</div>
              <p className="eco-stat__desc">
                Learners have already started building portable education identities through Open Campus ID.
              </p>
              <img className="eco-stat__ocid" src={ocid} alt="" aria-hidden="true" />
            </div>
          </div>
        </div>
      </div>
      </div>
    </section>
  );
}
